import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Checkbox,
  Space,
  message,
  Select,
  Empty,
  InputNumber,
  Tag,
} from "antd";
import { CheckOutlined, DownOutlined, RightOutlined } from "@ant-design/icons";
import { WorkRequestLine, ProjectTask } from "../types";
import dropdownApis from "../../../api/WorkOrderManagement/DropdownApis";
import workRequestApis from "../../../api/WorkOrderManagement/WorkRequestApis";

const { Option } = Select;

interface ProjectTaskSelectorProps {
  projectId?: string;
  onLinesSelected: (lines: WorkRequestLine[]) => void;
  selectedLines?: WorkRequestLine[];
  editingLine?: WorkRequestLine | null;
}

const ProjectTaskSelector: React.FC<ProjectTaskSelectorProps> = ({
  projectId,
  onLinesSelected,
  selectedLines = [],
  editingLine,
}) => {
  const [projectTasks, setProjectTasks] = useState<ProjectTask[]>([]);
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [taskQuantities, setTaskQuantities] = useState<{
    [key: string]: number;
  }>({});
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState<any[]>([]);
  const [selectedActivityId, setSelectedActivityId] = useState<string>("");

  useEffect(() => {
    if (projectId) {
      fetchActivities();
      fetchProjectTasks();
    }
  }, [projectId, editingLine]);

  useEffect(() => {
    // Initialize selected tasks if editing
    if (editingLine && editingLine.sourceId) {
      setSelectedTaskIds([editingLine.sourceId]);
      setTaskQuantities({
        [editingLine.sourceId]:
          (editingLine as any).requestedQuantity || editingLine.quantity,
      });
    } else if (selectedLines.length > 0) {
      const taskIds = selectedLines
        .filter((line) => line.sourceId)
        .map((line) => line.sourceId!);
      setSelectedTaskIds(taskIds);
    }
  }, [selectedLines, editingLine]);

  const fetchActivities = async () => {
    try {
      if (!projectId) return;

      const { data, status } = await dropdownApis.fetchActivityDropdown(
        parseInt(projectId)
      );
      if (status && data) {
        setActivities(data);
      }
    } catch (error) {
      console.log("Error fetching activities:", error);
      message.error("Failed to fetch activities for selected project");
    }
  };

  const fetchProjectTasks = async (activityId?: string) => {
    try {
      setLoading(true);

      let tasks: ProjectTask[] = [];
      if (activityId && projectId) {
        // Fetch tasks from API when activity is selected
        const params = {
          page: 1,
          pageSize: 15,
          direction: "P",
          sortOrder: "DESC",
          pageDiff: 0,
          projectIds: parseInt(projectId),
          activityIds: parseInt(activityId),
          derivedProjectStatus: "Active",
        };

        const { data, status }: any =
          await workRequestApis.taskListByActivityId(params);
        if (status && data && data.list) {
          tasks = data.list.map((task: any) => ({
            taskId: task.taskId?.toString() || task.id?.toString() || "",
            taskName: task.taskName || task.name || "",
            description: task.taskDescription || task.description || "",
            quantity: task.workQuantity,
            allocatedQty: task.requestedQty,
            pendingQty: task.workQuantity - task.requestedQty,
            uom: task.workUomName,
            uomId: task.workUomId,
            plannedStart: task.taskPlannedStartDate,
            plannedEnd: task.taskPlannedEndDate,
            activityId: activityId,
            category: task.workCategoryId,
            activityName:
              activities.find((a) => (a.activityId || a.id) === activityId)
                ?.activityName || "",
            taskBoqs: task.taskBoqs || [], // Include taskBoqs data
          }));
        }
      }

      setProjectTasks(tasks);
    } catch (error) {
      console.log("Error fetching project tasks:", error);
      message.error("Failed to fetch project tasks");
    } finally {
      setLoading(false);
    }
  };

  const handleActivityChange = (activityId: string) => {
    // In edit mode, don't allow activity changes
    if (editingLine) {
      message.warning("Activity cannot be changed in edit mode");
      return;
    }

    setSelectedActivityId(activityId);
    setSelectedTaskIds([]); // Clear selections when activity changes
    if (activityId) {
      fetchProjectTasks(activityId);
    } else {
      fetchProjectTasks(); // Fetch all tasks
    }
  };

  const handleTaskSelection = (taskId: string, checked: boolean) => {
    // In edit mode, don't allow task selection changes
    if (editingLine) {
      message.warning(
        "Task selection cannot be changed in edit mode. You can only modify the quantity."
      );
      return;
    }

    if (checked) {
      setSelectedTaskIds([...selectedTaskIds, taskId]);
      // Initialize with available quantity if not already set
      const task: any = projectTasks.find((t) => t.taskId === taskId);
      if (task && !taskQuantities[taskId]) {
        setTaskQuantities((prev) => ({ ...prev, [taskId]: task.pendingQty }));
      }
    } else {
      setSelectedTaskIds(selectedTaskIds.filter((id) => id !== taskId));
      // Remove quantity when deselected
      setTaskQuantities((prev: any) => {
        const updated = { ...prev };
        delete updated[taskId];
        return updated;
      });
    }
  };

  const handleSelectAll = (checked: boolean) => {
    // In edit mode, don't allow select all
    if (editingLine) {
      message.warning("Selection cannot be changed in edit mode");
      return;
    }

    if (checked) {
      const allTaskIds = projectTasks.map((task) => task.taskId);
      setSelectedTaskIds(allTaskIds);
      // Initialize quantities for all tasks
      const quantities: { [key: string]: number } = {};
      projectTasks.forEach((task) => {
        quantities[task.taskId] = taskQuantities[task.taskId] || task.quantity;
      });
      setTaskQuantities(quantities);
    } else {
      setSelectedTaskIds([]);
      setTaskQuantities({});
    }
  };

  const handleQuantityChange = (taskId: string, value: number | null) => {
    if (value && value > 0) {
      const task = projectTasks.find((t) => t.taskId === taskId);
      if (task && value <= task.quantity) {
        setTaskQuantities((prev) => ({ ...prev, [taskId]: value }));
      } else {
        message.error(
          `Requested quantity cannot exceed available quantity (${task?.quantity})`
        );
      }
    }
  };

  const handleAddSelected = () => {
    if (selectedTaskIds.length === 0) {
      message.warning("Please select at least one task");
      return;
    }

    // Validate quantities
    const invalidTasks = selectedTaskIds.filter((taskId) => {
      const requestedQty = taskQuantities[taskId];
      return !requestedQty || requestedQty <= 0;
    });

    if (invalidTasks.length > 0) {
      message.error("Please specify valid quantities for all selected tasks");
      return;
    }

    const selectedTaskObjects = projectTasks.filter((task) =>
      selectedTaskIds.includes(task.taskId)
    );

    console.log("selectedTaskObjects", selectedTaskObjects);
    // Convert ProjectTask to WorkRequestLine format
    const workRequestLines: any[] = selectedTaskObjects.map((task: any) => ({
      id: `${Date.now()}-${Math.random()}`,
      taskTitle: task.taskName,
      activityName: task.activityName,
      taskDescription: task.description,
      category: task.category, // Default category, can be improved with task categorization
      quantity: taskQuantities[task.taskId] || task.quantity,
      availableQuantity: task.quantity,
      allocatedQty: task.allocatedQty,
      pendingQty: task.pendingQty,
      requestedQuantity: taskQuantities[task.taskId] || task.quantity,
      uom: task.uom,
      uomId: task.uomId,
      source: "Project Management",
      sourceId: task.taskId,
      plannedStartDate: task.plannedStart,
      plannedEndDate: task.plannedEnd,
      taskBoqs: task.taskBoqs || [], // Include taskBoqs in the work request line
      // documents: [],
    }));

    onLinesSelected(workRequestLines);
  };

  // FSD Table Format: Select, Task ID, Task Name, Description, Quantity, UOM, Planned Start, Planned End
  const columns = [
    {
      title: "",
      key: "select",
      width: 50,
      render: (_: any, record: ProjectTask) => (
        <Checkbox
          checked={selectedTaskIds.includes(record.taskId)}
          onChange={(e) => handleTaskSelection(record.taskId, e.target.checked)}
          disabled={editingLine ? true : false}
        />
      ),
    },
    {
      title: "Task ID",
      dataIndex: "taskId",
      key: "taskId",
      width: 100,
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title: "Task Name",
      dataIndex: "taskName",
      key: "taskName",
      width: 280,
      render: (text: string, record: any) => {
        const hasTaskBoqs = record.taskBoqs && record.taskBoqs.length > 0;
        return (
          <div>
            <div className="fw-bold d-flex align-items-center gap-2">
              {text}
              {hasTaskBoqs && (
                <Tag color="purple">📋 BOQs ({record.taskBoqs.length})</Tag>
              )}
            </div>
            <small className="text-muted">{record.activityName}</small>
          </div>
        );
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 200,
      render: (text: string) => (
        <div style={{ wordWrap: "break-word", wordBreak: "break-word" }}>
          {text || "-"}
        </div>
      ),
    },
    {
      title: "BOQ Qty",
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      align: "right" as const,
      render: (quantity: number) => (
        <strong>{quantity?.toLocaleString()}</strong>
      ),
    },
    {
      title: "Allocated Qty",
      dataIndex: "allocatedQty",
      key: "allocatedQty",
      width: 100,
      align: "right" as const,
      render: (quantity: number) => (
        <strong>{quantity?.toLocaleString()}</strong>
      ),
    },
    {
      title: "Pending Qty",
      dataIndex: "pendingQty",
      key: "pendingQty",
      width: 100,
      align: "right" as const,
      render: (quantity: number) => (
        <strong>{quantity?.toLocaleString()}</strong>
      ),
    },
    {
      title: "Requested Qty",
      key: "requestedQty",
      width: 130,
      align: "center" as const,
      render: (_: any, record: any) => {
        const isSelected = selectedTaskIds.includes(record.taskId);
        const currentValue = taskQuantities[record.taskId] || record.pendingQty;

        return isSelected ? (
          <InputNumber
            min={1}
            max={record.pendingQty}
            value={currentValue}
            onChange={(value) => handleQuantityChange(record.taskId, value)}
            style={{ width: "100%" }}
            size="small"
          />
        ) : (
          <span className="text-muted">Select first</span>
        );
      },
    },
    {
      title: "UOM",
      dataIndex: "uom",
      key: "uom",
      width: 140,
      align: "center" as const,
    },
    {
      title: "Planned Start",
      dataIndex: "plannedStart",
      key: "plannedStart",
      width: 120,
      align: "center" as const,
    },
    {
      title: "Planned End",
      dataIndex: "plannedEnd",
      key: "plannedEnd",
      width: 120,
      align: "center" as const,
    },
  ];

  const isAllSelected =
    projectTasks.length > 0 && selectedTaskIds.length === projectTasks.length;
  const isIndeterminate =
    selectedTaskIds.length > 0 && selectedTaskIds.length < projectTasks.length;

  if (!projectId) {
    return (
      <Empty
        description="Please select a project in General Information section to view available tasks"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <div>
      {/* <div className="mb-3">
        <h6>📋 Select Tasks from Project Management</h6>
        <p className="text-muted">Choose tasks from project activities to create work request lines</p>
      </div> */}

      {/* Activity Filter - FSD Input Parameters */}
      <div className="mb-3">
        <label className="form-label">
          Activity <span className="text-danger">*</span>
        </label>
        <Select
          placeholder="Based on selected project, fetch activities"
          style={{ width: "100%" }}
          value={selectedActivityId}
          onChange={handleActivityChange}
          allowClear
        >
          {activities.map((activity) => (
            <Option
              key={activity.activityId || activity.id}
              value={activity.activityId || activity.id}
            >
              {activity.activityName || activity.name}
            </Option>
          ))}
        </Select>
        {/* <div className="form-note text-muted mt-1">
          FSD: Based on selected project, fetch activities
        </div> */}
      </div>

      {/* Tasks Table - FSD Display Format */}
      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          {!editingLine && (
            <>
              <div>
                <Checkbox
                  indeterminate={isIndeterminate}
                  checked={isAllSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  disabled={projectTasks.length === 0}
                >
                  Select All ({projectTasks.length} tasks)
                </Checkbox>
              </div>
              <div>
                <span className="text-muted small">
                  {selectedTaskIds.length} of {projectTasks.length} tasks
                  selected
                </span>
              </div>
            </>
          )}
          {editingLine && (
            <div className="w-100">
              <div className="alert alert-info mb-0">
                <strong>Edit Mode:</strong> Editing {editingLine.taskTitle} -
                Only quantity can be modified
              </div>
            </div>
          )}
        </div>

        <Table
          className="customTable"
          columns={columns}
          dataSource={projectTasks}
          loading={loading}
          pagination={false}
          scroll={{ x: "max-content" }}
          size="small"
          bordered
          rowKey="taskId"
          expandable={{
            expandedRowRender: (record: any) => {
              const taskBoqs = record.taskBoqs || [];
              if (!taskBoqs || taskBoqs.length === 0) {
                return null;
              }

              const boqColumns = [
                {
                  title: "Material Name",
                  dataIndex: "materialName",
                  key: "materialName",
                  width: "40%",
                  render: (name: string, material: any) => (
                    <div>
                      <div className="fw-bold">{name}</div>
                      {material.categoryName && (
                        <Tag color="blue" style={{ marginTop: 4 }}>
                          {material.categoryName}
                        </Tag>
                      )}
                    </div>
                  ),
                },
                // {
                //   title: "BOQ ID",
                //   dataIndex: "boqId",
                //   key: "boqId",
                //   width: "15%",
                //   render: (id: string) => (
                //     <code className="text-muted">{id}</code>
                //   ),
                // },
                // {
                //   title: "Category",
                //   dataIndex: "categoryName",
                //   key: "categoryName",
                //   width: "20%",
                //   render: (category: string) =>
                //     category ? <Tag color="blue">{category}</Tag> : "-",
                // },
                {
                  title: "Allocated Qty",
                  dataIndex: "allocatedQty",
                  key: "allocatedQty",
                  width: "20%",
                  align: "right" as const,
                  render: (qty: number) => (
                    <strong>{qty?.toLocaleString()}</strong>
                  ),
                },
                {
                  title: "UOM",
                  dataIndex: "uomName",
                  key: "uomName",
                  width: "20%",
                  align: "center" as const,
                  render: (uom: string) => <Tag color="geekblue">{uom}</Tag>,
                },
              ];

              return (
                <div
                  style={{
                    margin: "16px 0",
                    backgroundColor: "#fafafa",
                    padding: "16px",
                    borderRadius: "6px",
                  }}
                >
                  <div className="mb-2">
                    <strong>
                      Material Allocation ({taskBoqs.length} items)
                    </strong>
                  </div>
                  <Table
                    columns={boqColumns}
                    dataSource={taskBoqs}
                    pagination={false}
                    size="small"
                    rowKey={(record, index) =>
                      record.boqId || record.id || index
                    }
                    bordered
                  />
                </div>
              );
            },
            expandIcon: ({ expanded, onExpand, record }: any) => {
              const taskBoqs = record.taskBoqs || [];
              const hasTaskBoqs = taskBoqs && taskBoqs.length > 0;

              if (!hasTaskBoqs) return null;

              return expanded ? (
                <DownOutlined
                  onClick={(e) => onExpand(record, e)}
                  style={{ color: "#1890ff", cursor: "pointer" }}
                />
              ) : (
                <RightOutlined
                  onClick={(e) => onExpand(record, e)}
                  style={{ color: "#1890ff", cursor: "pointer" }}
                />
              );
            },
            rowExpandable: (record: any) => {
              const taskBoqs = record.taskBoqs || [];
              return taskBoqs && taskBoqs.length > 0;
            },
          }}
          locale={{
            emptyText: selectedActivityId
              ? "No tasks found for this activity"
              : "Please select an activity to view tasks",
          }}
        />
      </div>

      {/* Action Buttons */}
      <div className="d-flex justify-content-end">
        <Space>
          <Button
            type="primary"
            icon={<CheckOutlined />}
            onClick={handleAddSelected}
            disabled={selectedTaskIds.length === 0}
          >
            Add Selected Tasks ({selectedTaskIds.length})
          </Button>
        </Space>
      </div>

      {/* FSD Note */}
      {/* <div className="mt-3 p-2 bg-light rounded">
        <small className="text-muted">
          <strong>FSD Reference:</strong> Users can select multiple tasks from the above grid to be added to the Work Request.
          Table format: Select, Task ID, Task Name, Description, Quantity, UOM, Planned Start, Planned End
        </small>
      </div> */}
    </div>
  );
};

export default ProjectTaskSelector;
